import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/layout/ErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "@/components/ui/ThemeProvider";

// =========================================================================
// IMPOR HALAMAN (MENGGUNAKAN LAZY LOADING) ⚡
// =========================================================================
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const DocumentLibrary = lazy(() => import("@/pages/DocumentLibrary"));
const DocumentDetail = lazy(() => import("@/pages/DocumentDetail"));
const Approvals = lazy(() => import("@/pages/Approvals"));
const AuditTrail = lazy(() => import("@/pages/AuditTrail"));
const Users = lazy(() => import("@/pages/Users"));
const Departments = lazy(() => import("@/pages/Departments"));
const Reports = lazy(() => import("@/pages/Reports"));
const ArchiveList = lazy(() => import("@/pages/ArchiveList"));
const DelegationSettings = lazy(() => import('./pages/DelegationSettings'));
const CategoryManagement = lazy(() => import("@/pages/CategoryManagement")); 
const VerifyDocument = lazy(() => import("@/pages/VerifyDocument"));

// =========================================================================
// KOMPONEN FALLBACK & QUERY CLIENT
// =========================================================================
const PageFallback = () => (
  <div className="flex h-screen w-full items-center justify-center bg-gray-50/50">
    <div className="flex flex-col items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600 mb-3" />
      <p className="text-sm font-medium text-blue-600 animate-pulse">Memuat modul aplikasi...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data dianggap segar selama 5 menit
      retry: 1, // Jika API gagal, coba lagi 1 kali
      refetchOnWindowFocus: false, 
    },
  },
});

// =========================================================================
// KOMPONEN LISTENER WEBSOCKET (TIDAK BOLEH HILANG) 📡
// =========================================================================
function NotificationListener() {
  const { user } = useAuth(); 

  useEffect(() => {
    if ("Notification" in window) {
      if (Notification.permission !== "granted" && Notification.permission !== "denied") {
        Notification.requestPermission();
      }
    }
  }, []);

  useEffect(() => {
    if (!user) return; 

    let ws = null;
    let reconnectTimeout = null;
    let debounceTimeout = null;
    let isComponentMounted = true; 

    const connect = () => {
      console.log(`🚀 [DEBUG WS] Mencoba menyambungkan WebSocket untuk ${user.full_name}...`);

      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl = `${wsProtocol}//localhost:8001/api/ws/notifications`;
      
      ws = new WebSocket(wsUrl);

      ws.onopen = () => console.log("🟢 [WEBSOCKET] BERHASIL TERHUBUNG KE BACKEND!");

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const notifTitle = data.title || "Notifikasi Baru";
          const notifBody = data.message;
          
          toast.info(notifTitle, { description: notifBody });
          
          if ("Notification" in window && Notification.permission === "granted" && document.hidden) {
            const nativeNotif = new Notification(notifTitle, {
              body: notifBody,
              icon: "/favicon.ico", 
              silent: false 
            });

            nativeNotif.onclick = () => {
              window.focus();
              nativeNotif.close();
            };
          }
          
          if (debounceTimeout) {
            clearTimeout(debounceTimeout);
          }
          
          debounceTimeout = setTimeout(() => {
            console.log("📢 [DEBOUNCE] Meneriakkan REFRESH_DATA...");
            window.dispatchEvent(new Event("REFRESH_DATA"));
          }, 500);

        } catch (err) {
          console.error("Gagal membaca pesan WebSocket:", err);
        }
      };

      ws.onclose = (event) => {
        console.warn(`🟡 [WEBSOCKET] Terputus. Mencoba menyambung kembali...`);
        if (isComponentMounted) {
          reconnectTimeout = setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      isComponentMounted = false;
      clearTimeout(reconnectTimeout);
      if (debounceTimeout) clearTimeout(debounceTimeout);
      if (ws) ws.close();
    };
  }, [user]); 

  return null;
}

// =========================================================================
// KOMPONEN UTAMA APP
// =========================================================================
function App() {
  return (
    // 👇 1. PEMBUNGKUS QUERY CLIENT SUDAH TEPAT
    <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="light" storageKey="edms-theme">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          
          {/* Listenter WebSocket */}
          <NotificationListener />
          
          <ErrorBoundary>
          {/* BUNGKUS ROUTES DENGAN SUSPENSE UNTUK LAZY LOADING */}
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* RUTE PUBLIK */}
              <Route path="/login" element={<Login />} />
              <Route path="/verify/:id" element={<VerifyDocument />} />

              {/* RUTE PRIVAT */}
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              
              <Route path="/documents" element={<ProtectedRoute><DocumentLibrary /></ProtectedRoute>} />
              <Route path="/documents/:id" element={<ProtectedRoute><DocumentDetail /></ProtectedRoute>} />
              
              <Route path="/approvals" element={<ProtectedRoute allowedRoles={["manager", "manager_department", "head_department", "super_admin"]}><Approvals /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute allowedRoles={["manager_department", "admin_dokumen", "super_admin"]}><Reports /></ProtectedRoute>} />
              <Route path="/audit-trail" element={<ProtectedRoute allowedRoles={["manager_department", "admin_dokumen", "super_admin"]}><AuditTrail /></ProtectedRoute>} />
              
              <Route path="/users" element={<ProtectedRoute allowedRoles={["super_admin"]}><Users /></ProtectedRoute>} />
              <Route path="/departments" element={<ProtectedRoute allowedRoles={["super_admin"]}><Departments /></ProtectedRoute>} />
              <Route path="/categories" element={<ProtectedRoute allowedRoles={["super_admin"]}><CategoryManagement /></ProtectedRoute>} />
              
              <Route path="/settings/delegation" element={<ProtectedRoute><DelegationSettings /></ProtectedRoute>} />
              
              <Route path="/archives" element={<ProtectedRoute allowedRoles={["super administrator", "super_admin", "admin_dokumen", "manager_department", "head department", "head_department"]}><ArchiveList /></ProtectedRoute>} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
    </QueryClientProvider>
  );
}

export default App;